import React, { useState, useEffect } from 'react';
import Button from '../../../shared/components/Button';
import PhoneField from '../../../shared/components/PhoneField';
import { supabaseClient } from '../../../shared/utils/supabaseClient';
import { useParams, useNavigate } from 'react-router-dom';
import { createInviteToken } from '../../onboarding/utils/inviteToken';
import { buildInviteContent } from '../../onboarding/utils/inviteContent';
import { memberConstraintMessage } from '../../../shared/utils/memberConstraintError';

const AddMemberForm = ({ onMemberAdded }) => {
  const { groupId } = useParams();
  const navigate = useNavigate();
  const [groupData, setGroupData] = useState(null);
  const [inviteResult, setInviteResult] = useState(null);
  const [copied, setCopied] = useState(false);
  const [takenPositions, setTakenPositions] = useState([]);
  const [phoneMeta, setPhoneMeta] = useState({
    whatsapp: { isValid: false, isEmpty: true },
    sms: { isValid: false, isEmpty: true },
  });
  const [phoneResetKey, setPhoneResetKey] = useState(0);

  useEffect(() => {
    const fetchGroupData = async () => {
      const { data: group } = await supabaseClient
        .from('groups')
        .select('group_name, contribution_amount_per_hand, contribution_frequency, start_date')
        .eq('id', parseInt(groupId, 10))
        .single();
      setGroupData(group);
    };
    const fetchTakenPositions = async () => {
      const { data: hands } = await supabaseClient
        .from('hands')
        .select('position_number')
        .eq('group_id', parseInt(groupId, 10));
      setTakenPositions((hands || []).map(h => h.position_number).sort((a, b) => a - b));
    };
    if (groupId) {
      fetchGroupData();
      fetchTakenPositions();
    }
  }, [groupId]);

  const [formData, setFormData] = useState({
    name: '',
    whatsappNumber: '',
    smsNumber: '',
    email: '',
    notificationPreference: 'whatsapp_only',
    numberOfHands: '',
    rotationPositions: [],
    // Optional payment handles (direct groups) — admin may pre-fill if known; the
    // member can also set/edit them later in their own settings.
    zelleIdentifier: '',
    cashappHandle: '',
    venmoHandle: '',
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Member name is required';
    }
    if (phoneMeta.whatsapp.isEmpty) {
      newErrors.whatsappNumber = 'WhatsApp number is required';
    } else if (!phoneMeta.whatsapp.isValid) {
      newErrors.whatsappNumber = 'Enter a valid number for the selected country';
    }
    if (phoneMeta.sms.isEmpty) {
      newErrors.smsNumber = 'SMS number is required';
    } else if (!phoneMeta.sms.isValid) {
      newErrors.smsNumber = 'Enter a valid number for the selected country';
    }

    const numHands = parseInt(formData.numberOfHands, 10);
    if (isNaN(numHands) || numHands < 1 || numHands > 5) {
      newErrors.numberOfHands = 'Number of hands must be between 1 and 5';
    }
    if (formData.rotationPositions.length !== numHands) {
      newErrors.rotationPositions = 'Number of rotation positions must match number of hands';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setLoading(true);
    setError(null);

    try {
      const { data: { user } } = await supabaseClient.auth.getUser();
      const numericGroupId = parseInt(groupId, 10);

      if (!numericGroupId || isNaN(numericGroupId)) throw new Error('Invalid group ID');

      // Step 1: Reject within-group duplicates (phone / name / email).
      // Add Member ALWAYS creates a NEW member — it must never update or merge
      // onto an existing record. (Merging is what caused the Pilot1→Pilot67
      // identity/money corruption.) The database enforces this per group via the
      // partial unique indexes members_group_phone_uniq / _name_uniq / _email_uniq;
      // this pre-check just surfaces a clear, field-specific message first.
      const normName = formData.name.trim().toLowerCase();
      const normEmail = formData.email.trim().toLowerCase();
      const { data: groupMembers } = await supabaseClient
        .from('members')
        .select('name, whatsapp_number, sms_number, email, status')
        .eq('group_id', numericGroupId);
      // Exclude soft-removed members — matches the DB unique indexes
      // (status IS DISTINCT FROM 'removed'), so a previously-removed person
      // can be re-added without a false "already in this group" error.
      const roster = (groupMembers || []).filter(m => m.status !== 'removed');

      if (roster.some(m => m.whatsapp_number && m.whatsapp_number === formData.whatsappNumber)) {
        throw new Error('A member with this WhatsApp number is already in this group.');
      }
      // SMS is a SEPARATE field from WhatsApp (same person may use different
      // numbers) — enforced independently, per-field, both here and via the DB
      // index members_group_sms_uniq.
      if (roster.some(m => m.sms_number && m.sms_number === formData.smsNumber)) {
        throw new Error('A member with this SMS number is already in this group.');
      }
      if (roster.some(m => m.name && m.name.trim().toLowerCase() === normName)) {
        throw new Error(`A member named "${formData.name.trim()}" is already in this group.`);
      }
      if (normEmail && roster.some(m => m.email && m.email.trim().toLowerCase() === normEmail)) {
        throw new Error('A member with this email is already in this group.');
      }

      // Step 2: Create the NEW member (never reuse/update an existing row).
      const { data: newMember, error: memberError } = await supabaseClient
        .from('members')
        .insert({
          group_id: numericGroupId,
          name: formData.name,
          whatsapp_number: formData.whatsappNumber,
          sms_number: formData.smsNumber,
          email: formData.email || null,
          notification_preference: formData.notificationPreference,
          zelle_identifier: formData.zelleIdentifier?.trim() || null,
          cashapp_handle: formData.cashappHandle?.trim() || null,
          venmo_handle: formData.venmoHandle?.trim() || null,
          identity_verified: false,
          joined_date: new Date().toISOString().split('T')[0],
          status: 'active',
        })
        .select('id')
        .single();
      if (memberError) throw memberError;
      const memberId = newMember.id;

      // Step 3: Validate no duplicate rotation positions in this group
      const selectedPositions = formData.rotationPositions.map(p => parseInt(p, 10));
      const { data: existingPositions } = await supabaseClient
        .from('hands')
        .select('position_number')
        .eq('group_id', numericGroupId)
        .in('position_number', selectedPositions);

      if (existingPositions && existingPositions.length > 0) {
        const taken = existingPositions.map(h => h.position_number);
        const conflict = selectedPositions.find(p => taken.includes(p));
        throw new Error(`Position ${conflict} is already assigned to another member. Please choose a different position.`);
      }

      // Step 4: Create hands
      const handsPayload = selectedPositions.map(position => ({
        group_id: numericGroupId,
        member_id: null,
        member_db_id: memberId,
        position_number: position,
        contribution_amount: groupData?.contribution_amount_per_hand || 0,
        // contribution_status removed — per-round pending state now lives in
        // payments rows (susu_rounds); hands no longer carry money state.
      }));

      const { error: handsError } = await supabaseClient
        .from('hands')
        .insert(handsPayload);
      if (handsError) throw handsError;

      // Step 5: Create invite token
      const invite = await createInviteToken({
        groupId: numericGroupId,
        memberDbId: memberId,
        adminId: user.id,
      });

      const inviteUrl = `${window.location.origin}/invite/${invite.token}`;
      const adminName = user.user_metadata?.full_name || user.user_metadata?.name || user.email?.split('@')[0] || 'Your Group Admin';
      const firstDueDate = groupData?.start_date
        // Parse date-only at LOCAL midnight (avoid the UTC off-by-one).
        ? new Date(`${groupData.start_date}T00:00:00`).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
        : 'TBD';

      const content = buildInviteContent({
        groupName: groupData?.group_name || '',
        adminName,
        contributionAmount: groupData?.contribution_amount_per_hand || 0,
        frequency: groupData?.contribution_frequency || '',
        positions: formData.rotationPositions,
        firstDueDate,
        inviteUrl,
      });

      // Step 6: Try to send email
      let emailSent = false;
      if (formData.email) {
        try {
          const { error: fnError } = await supabaseClient.functions.invoke('send-invite-email', {
            body: {
              to: formData.email,
              subject: content.subject,
              html: content.html,
              text: content.text,
            },
          });
          emailSent = !fnError;
        } catch {
          emailSent = false;
        }
      }

      // Step 7: Audit log
      await supabaseClient.from('audit_logs').insert({
        event_category: 'member_management',
        event_type: 'invite_sent',
        actor_id: user.id,
        actor_role: 'admin',
        new_value: {
          invite_id: invite.id,
          group_id: numericGroupId,
          member_name: formData.name,
          email_sent: emailSent,
        },
        risk_level: 'low',
        flagged: false,
        timestamp: new Date().toISOString(),
        metadata: { invite_token: invite.token },
      });

      setInviteResult({ invite, inviteUrl, emailSent, whatsappText: content.text });
      onMemberAdded?.();
    } catch (err) {
      console.error('Add member error:', err);
      const msg = err?.message || '';
      // Shared member-identity constraint mapping (whatsapp/sms/email/name +
      // global phone anchor) — the SAME source of truth as Edit and preferences.
      const constraintMsg = memberConstraintMessage(err);
      if (constraintMsg) {
        setError(constraintMsg);
      } else if (err?.code === '23505') {
        // Remaining 23505 cases specific to this flow (not member identity).
        if (msg.includes('unique_group_position')) {
          // hands UNIQUE(group_id, position_number) — a position was taken
          // between the pre-check and the insert.
          setError('One or more positions were just taken. Please refresh and choose different positions.');
        } else {
          setError('That member conflicts with an existing one in this group. Please check the details and try again.');
        }
      } else if (err?.message) {
        // Preserve the real message (e.g. the friendly duplicate pre-check error).
        setError(err.message);
      } else {
        setError('Failed to add member. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  // Store the normalized E.164 (or '' when incomplete/invalid) and track validity.
  const handlePhoneChange = (which) => ({ e164, isValid, isEmpty }) => {
    const field = which === 'whatsapp' ? 'whatsappNumber' : 'smsNumber';
    setFormData(prev => ({ ...prev, [field]: e164 }));
    setPhoneMeta(prev => ({ ...prev, [which]: { isValid, isEmpty } }));
  };

  // First `count` positions that are not already taken by the group.
  const firstAvailablePositions = (count) => {
    const taken = new Set(takenPositions);
    const result = [];
    let p = 1;
    while (result.length < count) {
      if (!taken.has(p)) result.push(p);
      p += 1;
    }
    return result;
  };

  const handleHandCountChange = (raw) => {
    const num = parseInt(raw, 10);
    const valid = !isNaN(num) && num >= 1 && num <= 5;
    setFormData(prev => ({
      ...prev,
      numberOfHands: raw,
      // Auto-suggest defaults that skip already-taken positions.
      rotationPositions: valid ? firstAvailablePositions(num).map(String) : [],
    }));
  };

  // ── Rotation position validity (taken / duplicate) ──────────────────────────
  const takenSet = new Set(takenPositions);
  const positionCounts = {};
  formData.rotationPositions.forEach(v => {
    const n = parseInt(v, 10);
    if (!isNaN(n)) positionCounts[n] = (positionCounts[n] || 0) + 1;
  });
  const positionIssues = formData.rotationPositions.map(v => {
    const n = parseInt(v, 10);
    if (v === '' || isNaN(n)) return null;
    if (n < 1) return 'Must be ≥ 1';
    if (takenSet.has(n)) return `Position ${n} is taken`;
    if (positionCounts[n] > 1) return `Position ${n} is a duplicate`;
    return null;
  });
  const positionsInvalid =
    formData.rotationPositions.length === 0 ||
    formData.rotationPositions.some(v => v === '' || isNaN(parseInt(v, 10))) ||
    positionIssues.some(Boolean);
  const nextAvailablePosition = firstAvailablePositions(1)[0];

  // Reset the form for adding another member, refreshing taken positions so the
  // picker reflects the member that was just added.
  const handleAddAnother = async () => {
    setInviteResult(null);
    setCopied(false);
    setErrors({});
    setError(null);
    setFormData({
      name: '',
      whatsappNumber: '',
      smsNumber: '',
      email: '',
      notificationPreference: 'whatsapp_only',
      numberOfHands: '',
      rotationPositions: [],
    });
    setPhoneMeta({
      whatsapp: { isValid: false, isEmpty: true },
      sms: { isValid: false, isEmpty: true },
    });
    setPhoneResetKey(k => k + 1);
    const { data: hands } = await supabaseClient
      .from('hands')
      .select('position_number')
      .eq('group_id', parseInt(groupId, 10));
    setTakenPositions((hands || []).map(h => h.position_number).sort((a, b) => a - b));
  };

  // ── Invite sent — show result UI ────────────────────────────────────────────
  if (inviteResult) {
    const { inviteUrl, emailSent, whatsappText } = inviteResult;
    const waLink = `https://wa.me/?text=${encodeURIComponent(whatsappText)}`;

    return (
      <div className="bg-neutral-100 rounded-lg shadow-sm border border-neutral-200 p-6">
        <div className="text-center mb-6">
          <div className="w-14 h-14 bg-success-100 rounded-full flex items-center justify-center mx-auto mb-3">
            <svg className="w-7 h-7 text-success-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h2 className="text-lg font-semibold text-neutral-900">Member Added</h2>
          <p className="text-sm text-neutral-600 mt-1">
            {emailSent
              ? `Invite email sent to ${formData.email}`
              : formData.email
                ? 'Email could not be delivered — share the link below instead'
                : 'Share the invite link with the member'}
          </p>
        </div>

        {!emailSent && (
          <div className="space-y-4">
            <div>
              <label className="block text-xs font-medium text-neutral-500 mb-1">Invite link</label>
              <div className="flex items-center space-x-2">
                <input
                  type="text"
                  readOnly
                  value={inviteUrl}
                  className="flex-1 px-3 py-2 text-sm border border-neutral-300 rounded-md bg-neutral-50 text-neutral-700"
                  onClick={e => e.target.select()}
                />
                <button
                  type="button"
                  onClick={async () => {
                    try {
                      await navigator.clipboard.writeText(inviteUrl);
                      setCopied(true);
                      setTimeout(() => setCopied(false), 2000);
                    } catch {
                      // clipboard unavailable — leave label unchanged
                    }
                  }}
                  className="px-3 py-2 text-sm border border-neutral-300 rounded-md hover:bg-neutral-50 text-neutral-600"
                >
                  {copied ? '✓ Copied!' : 'Copy'}
                </button>
              </div>
            </div>

            <a
              href={waLink}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-center space-x-2 w-full px-4 py-3 bg-success-600 text-white rounded-lg font-medium hover:bg-success-700"
            >
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
              </svg>
              <span>Share via WhatsApp</span>
            </a>
          </div>
        )}

        <div className="mt-6 text-center">
          <p className="text-xs text-neutral-400">Link expires in 7 days</p>
        </div>

        <div className="mt-6 flex flex-col sm:flex-row gap-3">
          <Button
            type="button"
            variant="primary"
            size="lg"
            className="flex-1"
            onClick={() => navigate(`/admin/group/${groupId}/payments`)}
          >
            ← Back to Dashboard
          </Button>
          <Button
            type="button"
            variant="secondary"
            size="lg"
            className="flex-1"
            onClick={handleAddAnother}
          >
            Add Another Member
          </Button>
        </div>
      </div>
    );
  }

  // ── Add member form ─────────────────────────────────────────────────────────
  return (
    <div className="bg-neutral-100 rounded-lg shadow-sm border border-neutral-200 p-6">
      <h2 className="text-lg font-semibold text-neutral-900 mb-6">Add New Member</h2>

      <form onSubmit={onSubmit} className="space-y-6">
        {/* Member name — a display label so you can identify the invitee. The
            member enters their own legal name when they sign the agreement. */}
        <div>
          <label className="block text-sm font-medium text-neutral-700 mb-2">Member name *</label>
          <input
            type="text"
            value={formData.name}
            onChange={(e) => handleInputChange('name', e.target.value)}
            className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            placeholder="How you'll recognize them, e.g. Chioma"
            disabled={loading}
          />
          <p className="text-xs text-neutral-500 mt-1">The member confirms their full legal name when they accept the agreement.</p>
          {errors.name && <p className="text-error-600 text-sm mt-1">{errors.name}</p>}
        </div>

        {/* WhatsApp Number */}
        <div>
          <label className="block text-sm font-medium text-neutral-700 mb-2">WhatsApp Number *</label>
          <PhoneField
            key={`wa-${phoneResetKey}`}
            id="whatsapp"
            defaultCountry="US"
            onChange={handlePhoneChange('whatsapp')}
            error={!!errors.whatsappNumber}
            disabled={loading}
            placeholder="WhatsApp number"
          />
          {errors.whatsappNumber && <p className="text-error-600 text-sm mt-1">{errors.whatsappNumber}</p>}
        </div>

        {/* SMS Number */}
        <div>
          <label className="block text-sm font-medium text-neutral-700 mb-2">
            SMS Number *
            <span className="text-xs text-neutral-500 ml-1">(May differ from WhatsApp)</span>
          </label>
          <PhoneField
            key={`sms-${phoneResetKey}`}
            id="sms"
            defaultCountry="US"
            onChange={handlePhoneChange('sms')}
            error={!!errors.smsNumber}
            disabled={loading}
            placeholder="SMS number"
          />
          {errors.smsNumber && <p className="text-error-600 text-sm mt-1">{errors.smsNumber}</p>}
        </div>

        {/* Email */}
        <div>
          <label className="block text-sm font-medium text-neutral-700 mb-2">
            Email
            <span className="text-xs text-neutral-500 ml-1">(Invite email will be sent here)</span>
          </label>
          <input
            type="email"
            value={formData.email}
            onChange={(e) => handleInputChange('email', e.target.value)}
            className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            placeholder="member@example.com"
            disabled={loading}
          />
          {errors.email && <p className="text-error-600 text-sm mt-1">{errors.email}</p>}
        </div>

        {/* Payment handles (optional; direct groups). Admin may pre-fill; the member
            can set/edit them later in their own settings. */}
        <div>
          <label className="block text-sm font-medium text-neutral-700 mb-2">
            Payment Details
            <span className="text-xs text-neutral-500 ml-1">(optional — for direct-payment groups)</span>
          </label>
          <input
            type="text"
            value={formData.zelleIdentifier}
            onChange={(e) => handleInputChange('zelleIdentifier', e.target.value)}
            className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500 mb-2"
            placeholder="Zelle (email or phone)"
            disabled={loading}
          />
          <input
            type="text"
            value={formData.cashappHandle}
            onChange={(e) => handleInputChange('cashappHandle', e.target.value)}
            className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500 mb-2"
            placeholder="Cash App ($cashtag)"
            disabled={loading}
          />
          <input
            type="text"
            value={formData.venmoHandle}
            onChange={(e) => handleInputChange('venmoHandle', e.target.value)}
            className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            placeholder="Venmo (@handle)"
            disabled={loading}
          />
        </div>

        {/* Notification Preference */}
        <div>
          <label className="block text-sm font-medium text-neutral-700 mb-2">Notification Preference</label>
          <select
            value={formData.notificationPreference}
            onChange={(e) => handleInputChange('notificationPreference', e.target.value)}
            className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            disabled={loading}
          >
            <option value="whatsapp_only">WhatsApp only</option>
            <option value="whatsapp_sms">WhatsApp + SMS</option>
            <option value="sms_only">SMS only</option>
            <option value="in_app_only">In-app only</option>
          </select>
        </div>

        {/* Number of Hands */}
        <div>
          <label className="block text-sm font-medium text-neutral-700 mb-2">Number of Hands *</label>
          <input
            type="number"
            min="1"
            max="5"
            value={formData.numberOfHands}
            onChange={(e) => handleHandCountChange(e.target.value)}
            className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            disabled={loading}
          />
          {errors.numberOfHands && <p className="text-error-600 text-sm mt-1">{errors.numberOfHands}</p>}
        </div>

        {/* Rotation Positions */}
        <div>
          <label className="block text-sm font-medium text-neutral-700 mb-2">Rotation Position(s)</label>
          <div className="space-y-2">
            {formData.rotationPositions.map((position, index) => {
              const issue = positionIssues[index];
              return (
                <div key={index} className="flex items-center space-x-2">
                  <span className="text-sm text-neutral-600">Hand {index + 1}:</span>
                  <input
                    type="number"
                    min="1"
                    value={position}
                    onChange={(e) => {
                      const newPositions = [...formData.rotationPositions];
                      newPositions[index] = e.target.value;
                      handleInputChange('rotationPositions', newPositions);
                    }}
                    className={`w-20 px-2 py-1 border rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-sm ${issue ? 'border-error-500' : 'border-neutral-300'}`}
                    disabled={loading}
                  />
                  {issue && <span className="text-xs text-error-600">{issue}</span>}
                </div>
              );
            })}
          </div>
          {/* Taken / next-available helper */}
          <p className="text-xs text-neutral-500 mt-2">
            {takenPositions.length > 0
              ? `Taken: ${takenPositions.join(', ')}`
              : 'No positions assigned yet.'}
            {' · '}Next available: {nextAvailablePosition}
          </p>
          {errors.rotationPositions && <p className="text-error-600 text-sm mt-1">{errors.rotationPositions}</p>}
        </div>

        {error && (
          <div className="bg-error-50 border border-error-200 rounded-lg p-4">
            <p className="text-error-600 text-sm">{error}</p>
          </div>
        )}

        <div className="flex gap-3">
          <Button
            type="button"
            variant="secondary"
            size="lg"
            className="flex-1"
            onClick={() => navigate(-1)}
            disabled={loading}
          >
            ← Cancel
          </Button>
          <Button type="submit" variant="primary" size="lg" disabled={loading || positionsInvalid} className="flex-1">
            {loading ? 'Adding Member...' : 'Add Member & Send Invite'}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default AddMemberForm;
